import React, { useEffect, useState } from "react";
import { MessageSquare, CheckCircle2, Info, Send, AlertTriangle } from "lucide-react";
import { adminApi } from "../../lib/api";
import { PageHeader, SectionHeader, Kpi, Card, pct } from "./adminUi";

/**
 * SMS Analytics — Twilio A2P 10DLC is APPROVED.
 *
 * Master Admin can send ONE locked test message to a single opted-in user
 * to verify Twilio wiring end-to-end. Automated outbound SMS triggers
 * (gifts, reminders, notifications, Proverb) remain intentionally
 * DISABLED at the app layer.
 *
 * Delivery counters are read from real send records and Twilio's
 * delivery-status webhook. Zero-state renders as "0", not "—".
 */
export default function AdminSmsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  // Send-Test state
  const [recipients, setRecipients] = useState(null);
  const [recipientsErr, setRecipientsErr] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendErr, setSendErr] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const load = () => {
    adminApi
      .smsAnalytics()
      .then(setData)
      .catch((e) =>
        setErr(e?.response?.data?.detail || "Unable to load SMS analytics")
      );
    adminApi
      .smsEligibleRecipients()
      .then((r) => setRecipients(r?.recipients || []))
      .catch((e) =>
        setRecipientsErr(
          e?.response?.data?.detail || "Unable to load eligible recipients"
        )
      );
  };

  useEffect(() => {
    load();
  }, []);

  const optins = data?.opt_ins || {};
  const optouts = data?.opt_outs || {};
  const d = data?.delivery || {};

  const selected = (recipients || []).find((u) => u.user_id === selectedUserId);
  const canSend = !!selected && !sending;

  const openConfirm = () => {
    if (!selected) return;
    setSendResult(null);
    setSendErr(null);
    setShowConfirm(true);
  };

  const doSend = async () => {
    if (!selected) return;
    setSending(true);
    setSendErr(null);
    setSendResult(null);
    try {
      const resp = await adminApi.smsSendTest({ user_id: selected.user_id });
      setSendResult(resp);
      setShowConfirm(false);
      // Refresh delivery counters and eligible-list (recipient could opt out).
      setTimeout(load, 800);
    } catch (e) {
      setSendErr(
        e?.response?.data?.detail || "Failed to send test SMS. Check Twilio configuration."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-sms-page" className="space-y-8">
      <PageHeader
        eyebrow="Compliance"
        title="SMS"
        subtitle="Live opt-in / opt-out analytics from the LifeGuide users collection. Automated outbound triggers remain disabled — only the Master-Admin test send below is active."
      />

      {/* A2P Approved banner */}
      <div
        data-testid="sms-a2p-banner"
        className="rounded-2xl border border-[#4F6F52]/50 bg-[#0F1B14] text-[#B7D2AF] px-5 py-4 flex items-start gap-3"
      >
        <CheckCircle2 size={18} className="mt-0.5 text-[#8FBF87]" />
        <div>
          <div className="font-semibold text-white" data-testid="sms-a2p-banner-title">
            Twilio A2P — Approved
          </div>
          <div className="text-[#8FA894] text-xs mt-1 leading-relaxed max-w-3xl">
            LifeGuide's A2P 10DLC registration is approved. All automated
            outbound SMS remains intentionally disabled at the app layer.
            The Master-Admin test send below is the only outbound path
            currently wired and is limited to a single opted-in user at
            a time.
          </div>
        </div>
      </div>

      {err && (
        <div className="rounded-2xl border border-[#B4795C]/50 bg-[#241914] text-[#F3D6A6] p-4 text-sm">
          {err}
        </div>
      )}

      {/* Send Test SMS (Master-Admin only) */}
      <section data-testid="sms-test-send-section">
        <SectionHeader
          label="Send Test SMS"
          right={
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#B7D2AF] bg-white/5 border border-white/10 rounded-full px-2.5 py-1">
              <Info size={11} /> Master Admin only
            </span>
          }
        />
        <Card className="space-y-4">
          <div className="text-sm text-[#B7D2AF] leading-relaxed max-w-3xl">
            Sends the following exact message to a single opted-in LifeGuide
            user to verify Twilio wiring. Free-form input, bulk send, and
            automated triggers are all disabled.
          </div>
          <div
            data-testid="sms-test-preview"
            className="rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-[#E9F1E4] font-mono"
          >
            LifeGuide: This is a test of your LifeGuide text notifications.
            Reply STOP to opt out or HELP for help.
          </div>

          {recipientsErr && (
            <div className="rounded-xl border border-[#B4795C]/50 bg-[#241914] text-[#F3D6A6] px-3 py-2 text-xs">
              {recipientsErr}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
            <label className="block">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#8FA894] mb-1.5">
                Opted-in recipient
              </div>
              <select
                data-testid="sms-test-recipient-select"
                value={selectedUserId}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                  setSendResult(null);
                  setSendErr(null);
                }}
                className="w-full rounded-xl bg-[#0E1613] border border-white/10 text-white text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#4F6F52]/60"
              >
                <option value="">
                  {recipients === null
                    ? "Loading opted-in users…"
                    : (recipients || []).length === 0
                    ? "No opted-in users available"
                    : "Select a user…"}
                </option>
                {(recipients || []).map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.email} · ••••{u.phone_last4}
                    {u.is_test ? " (test)" : ""}
                  </option>
                ))}
              </select>
            </label>
            <button
              data-testid="sms-test-send-button"
              type="button"
              disabled={!canSend}
              onClick={openConfirm}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#4F6F52] hover:bg-[#5A7E5D] disabled:bg-[#2A3A2D] disabled:text-[#6B7A6B] text-white text-sm font-semibold px-4 py-2.5 transition-colors"
            >
              <Send size={14} /> Send Test SMS
            </button>
          </div>

          {sendResult?.ok && (
            <div
              data-testid="sms-test-success"
              className="rounded-xl border border-[#4F6F52]/60 bg-[#0F1B14] text-[#B7D2AF] px-4 py-3 text-sm flex items-start gap-2"
            >
              <CheckCircle2 size={16} className="mt-0.5 text-[#8FBF87]" />
              <div>
                <div className="text-white font-semibold">Test SMS accepted by Twilio</div>
                <div className="text-xs text-[#8FA894] mt-1">
                  SID <code>{sendResult.message_sid}</code> · status{" "}
                  <code>{sendResult.status || "queued"}</code> · sent to ••••
                  {sendResult.phone_last4}. Delivery status will update in the
                  metrics below as Twilio reports back.
                </div>
              </div>
            </div>
          )}

          {sendErr && (
            <div
              data-testid="sms-test-error"
              className="rounded-xl border border-[#B4795C]/60 bg-[#241914] text-[#F3D6A6] px-4 py-3 text-sm flex items-start gap-2"
            >
              <AlertTriangle size={16} className="mt-0.5 text-[#E7A26B]" />
              <div>{sendErr}</div>
            </div>
          )}
        </Card>
      </section>

      <section>
        <SectionHeader label="Opt-ins" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="sms-optins" label="SMS opt-ins (total)" value={optins.total} tone="accent" />
          <Kpi testid="sms-optins-7d" label="New opt-ins — 7d" value={optins.new_7d} />
          <Kpi testid="sms-optins-30d" label="New opt-ins — 30d" value={optins.new_30d} />
        </div>
      </section>

      <section>
        <SectionHeader label="Opt-outs" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="sms-optouts" label="SMS opt-outs (total)" value={optouts.total} />
          <Kpi testid="sms-optouts-7d" label="New opt-outs — 7d" value={optouts.new_7d} />
          <Kpi testid="sms-optouts-30d" label="New opt-outs — 30d" value={optouts.new_30d} />
        </div>
      </section>

      <section>
        <SectionHeader
          label="Delivery"
          right={
            <span
              data-testid="sms-delivery-live-badge"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#B7D2AF] bg-white/5 border border-white/10 rounded-full px-2.5 py-1"
              title="Real counters from sms_messages + inbound Twilio webhook"
            >
              <CheckCircle2 size={11} /> Live counters
            </span>
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="sms-sent" label="Messages sent" value={d.messages_sent} />
          <Kpi testid="sms-delivered" label="Messages delivered" value={d.messages_delivered} />
          <Kpi
            testid="sms-delivery-rate"
            label="Delivery rate"
            value={d.delivery_rate_pct != null ? pct(d.delivery_rate_pct) : null}
          />
          <Kpi testid="sms-failed" label="Failed messages" value={d.messages_failed} />
          <Kpi testid="sms-stop" label="STOP requests" value={d.stop_requests} />
          <Kpi testid="sms-help" label="HELP requests" value={d.help_requests} />
        </div>
      </section>

      <section>
        <SectionHeader label="Compliance references" />
        <Card className="text-sm text-[#B7D2AF] leading-relaxed">
          <div className="flex items-start gap-3">
            <MessageSquare size={16} className="mt-0.5 text-[#B7D2AF]" />
            <div>
              The public opt-in page at <code>/sms</code> follows Twilio's A2P
              guidance: consent checkbox defaults off, phone number is
              optional, a prominent "No thanks — continue without text
              messages" button lets any visitor bypass the form, and
              enrollment is technically impossible until both the phone and
              consent are supplied voluntarily. Marketing/promotional consent
              is never combined with informational/service SMS consent.
            </div>
          </div>
        </Card>
      </section>

      {/* Confirm modal */}
      {showConfirm && selected && (
        <div
          data-testid="sms-test-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={() => !sending && setShowConfirm(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-white/10 bg-[#141B18] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-display text-xl">Send test SMS?</h3>
            <p className="text-sm text-[#B7D2AF] mt-2">
              A single test message will be sent to{" "}
              <span className="text-white font-semibold">{selected.email}</span>{" "}
              at <span className="text-white font-semibold">••••{selected.phone_last4}</span>.
            </p>
            <div className="mt-4 rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-xs text-[#E9F1E4] font-mono">
              LifeGuide: This is a test of your LifeGuide text notifications.
              Reply STOP to opt out or HELP for help.
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                data-testid="sms-test-confirm-cancel"
                disabled={sending}
                onClick={() => setShowConfirm(false)}
                className="text-sm text-[#B7D2AF] hover:text-white px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="sms-test-confirm-send"
                disabled={sending}
                onClick={doSend}
                className="inline-flex items-center gap-2 rounded-xl bg-[#4F6F52] hover:bg-[#5A7E5D] disabled:bg-[#2A3A2D] text-white text-sm font-semibold px-4 py-2"
              >
                <Send size={14} /> {sending ? "Sending…" : "Confirm & send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
